/**
 * useChecklistSuggestions Hook
 *
 * Provides access to suggested checklist items for a document
 * and mutations to accept/reject/dismiss them.
 */

import { useTranslation } from "react-i18next";
import { api } from "convex/_generated/api";
import { useMemo } from "react";
import type { Id } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";
import { useDocumentProcessingStatusSingle } from "@/hooks/use-document-processing-status";

export interface ResolvedSuggestion {
  checklistItemId: Id<"checklistItems">;
  score: number;
  confidence: string;
  reason: string;
  relevantExcerpt: string;
  title: string;
  categoryId: Id<"checklistCategories"> | null;
  categoryName: string;
  auditId: Id<"audits"> | null;
  auditName: string;
  clauseRefs: string[];
  exampleDocuments: Array<{ name: string; content: string }>;
}

export function useChecklistSuggestions(
  documentId: Id<"documents"> | null | undefined,
) {
  const { t } = useTranslation("audits");
  const workspaceId = useWorkspaceId();
  const { status: processingStatus } = useDocumentProcessingStatusSingle(
    documentId ?? undefined,
  );

  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getSuggestedChecklistDetails,
    { documentId: documentId ?? null },
  );

  const suggestions: ResolvedSuggestion[] = useMemo(() => {
    if (!data?.suggestions) return [];
    return data.suggestions;
  }, [data]);

  const acceptMutation = useApiMutation(
    api.document_mutations.acceptChecklistSuggestions,
    {
      name: "accept-checklist-suggestions",
      successMessage: t("suggestions.successAccept"),
    },
  );

  const dismissMutation = useApiMutation(
    api.document_mutations.dismissAllSuggestions,
    {
      name: "dismiss-checklist-suggestions",
      successMessage: t("suggestions.successDismiss"),
    },
  );

  const retryMutation = useApiMutation(
    api.document_mutations.retryFindMatchingChecklistItems,
    { name: "retry-checklist-matching" },
  );

  const acceptSuggestions = async (
    docId: Id<"documents">,
    acceptedIds: Id<"checklistItems">[],
    rejectedIds: Id<"checklistItems">[],
  ) => {
    await acceptMutation.mutateAsync({
      documentId: docId,
      acceptedItemIds: acceptedIds,
      rejectedItemIds: rejectedIds,
      workspaceId,
    });
  };

  const dismissAll = async (docId: Id<"documents">) => {
    await dismissMutation.mutateAsync({ documentId: docId, workspaceId });
  };

  const retryMatching = async (
    docId: Id<"documents">,
    auditId: Id<"audits">,
  ) => {
    await retryMutation.mutateAsync({
      documentId: docId,
      auditId,
      workspaceId,
    });
  };

  return {
    suggestions,
    isLoading,
    acceptSuggestions,
    dismissAll,
    isAccepting: acceptMutation.isPending || dismissMutation.isPending,
    suggestedChecklistItemsStatus:
      processingStatus?.suggestedChecklistItemsStatus,
    retryMatching,
    isRetrying: retryMutation.isPending,
  };
}
